<template>
  <Teleport to="body">
    <!-- Overlay bloqueante: solo cuando NO está en la ruta del paso actual -->
    <div
      v-if="showWizard && !isOnCurrentStepRoute"
      class="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm"
    >
      <div class="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div class="relative px-6 py-5 bg-gray-950 overflow-hidden">
          <div class="absolute inset-0 opacity-[0.04]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>
          <div class="relative z-10">
            <p class="text-indigo-400 text-xs font-semibold uppercase tracking-widest mb-1">
              {{ t('agentDashboard.onboarding.wizard.badge') }}
            </p>
            <h2 class="text-xl font-bold text-white">{{ t('agentDashboard.onboarding.wizard.title') }}</h2>
            <p class="text-gray-400 text-sm mt-1">{{ t('agentDashboard.onboarding.wizard.subtitle') }}</p>
          </div>
        </div>

        <div class="flex gap-1 px-6 pt-4">
          <div
            v-for="ws in wizardSteps"
            :key="ws.id"
            class="h-1 flex-1 rounded-full transition-colors"
            :class="ws.done ? 'bg-emerald-500' : (ws.id === wizardStep ? 'bg-indigo-500' : 'bg-gray-200')"
          />
        </div>

        <div class="px-6 py-6">
          <div class="flex items-start gap-4 mb-6">
            <div class="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0 text-2xl">
              {{ currentMeta.icon }}
            </div>
            <div>
              <p class="text-xs font-semibold text-indigo-600 mb-0.5">
                {{ t('agentDashboard.onboarding.wizard.stepOf', { current: currentStepNumber, total: 3 }) }}
              </p>
              <h3 class="text-lg font-bold text-gray-900">{{ t(currentMeta.titleKey) }}</h3>
              <p class="text-sm text-gray-500 mt-1 leading-relaxed">{{ t(currentMeta.descriptionKey) }}</p>
            </div>
          </div>

          <ul v-if="currentMeta.tips" class="mb-6 space-y-2">
            <li v-for="(tip, i) in currentMeta.tips" :key="i" class="flex items-start gap-2 text-xs text-gray-600">
              <span class="text-indigo-400 mt-0.5">•</span>
              {{ t(tip) }}
            </li>
          </ul>

          <router-link
            :to="currentStep?.route || '/agent-dashboard/profile'"
            class="flex w-full items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors"
          >
            {{ t(currentMeta.ctaKey) }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>

          <p class="text-[11px] text-gray-400 text-center mt-4">
            {{ t('agentDashboard.onboarding.wizard.mandatory') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Banner compacto en la ruta del paso (permite interactuar con el formulario) -->
    <div
      v-else-if="showWizard && isOnCurrentStepRoute"
      class="fixed top-16 left-0 right-0 z-[200] px-4 py-2 bg-gray-950/95 border-b border-gray-800 shadow-lg"
    >
      <div class="max-w-4xl mx-auto flex items-center justify-between gap-3">
        <div class="min-w-0">
          <p class="text-[10px] font-semibold text-indigo-400 uppercase tracking-widest">
            {{ t('agentDashboard.onboarding.wizard.stepOf', { current: currentStepNumber, total: 3 }) }}
          </p>
          <p class="text-sm font-semibold text-white truncate">{{ t(currentMeta.titleKey) }}</p>
        </div>
        <div class="flex gap-1 flex-shrink-0">
          <div
            v-for="ws in wizardSteps"
            :key="ws.id"
            class="h-1.5 w-8 rounded-full"
            :class="ws.done ? 'bg-emerald-500' : (ws.id === wizardStep ? 'bg-indigo-400' : 'bg-gray-600')"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSharedAgentOnboarding } from '@/composables/useAgentOnboarding'

const { t } = useI18n()
const route = useRoute()
const { showWizard, wizardStep, steps } = useSharedAgentOnboarding()

const WIZARD_IDS = ['profile', 'firstColiving', 'published']

const wizardSteps = computed(() =>
  steps.value.filter(s => WIZARD_IDS.includes(s.id))
)

const currentStep = computed(() =>
  wizardSteps.value.find(s => s.id === wizardStep.value) || wizardSteps.value[0]
)

const currentStepNumber = computed(() => {
  const idx = WIZARD_IDS.indexOf(wizardStep.value)
  return idx >= 0 ? idx + 1 : 1
})

const STEP_META = {
  profile: {
    icon: '👤',
    titleKey: 'agentDashboard.onboarding.wizard.steps.profile.title',
    descriptionKey: 'agentDashboard.onboarding.wizard.steps.profile.description',
    ctaKey: 'agentDashboard.onboarding.wizard.steps.profile.cta',
    tips: [
      'agentDashboard.onboarding.wizard.steps.profile.tip1',
      'agentDashboard.onboarding.wizard.steps.profile.tip2',
    ],
  },
  firstColiving: {
    icon: '🏠',
    titleKey: 'agentDashboard.onboarding.wizard.steps.firstColiving.title',
    descriptionKey: 'agentDashboard.onboarding.wizard.steps.firstColiving.description',
    ctaKey: 'agentDashboard.onboarding.wizard.steps.firstColiving.cta',
    tips: [
      'agentDashboard.onboarding.wizard.steps.firstColiving.tip1',
      'agentDashboard.onboarding.wizard.steps.firstColiving.tip2',
    ],
  },
  published: {
    icon: '🚀',
    titleKey: 'agentDashboard.onboarding.wizard.steps.published.title',
    descriptionKey: 'agentDashboard.onboarding.wizard.steps.published.description',
    ctaKey: 'agentDashboard.onboarding.wizard.steps.published.cta',
    tips: [
      'agentDashboard.onboarding.wizard.steps.published.tip1',
      'agentDashboard.onboarding.wizard.steps.published.tip2',
    ],
  },
}

const currentMeta = computed(() => STEP_META[wizardStep.value] || STEP_META.profile)

/** Ocultar overlay en la ruta del paso para que el agente pueda usar el formulario */
const isOnCurrentStepRoute = computed(() => {
  const stepRoute = currentStep.value?.route
  if (!stepRoute) return false
  return route.path === stepRoute || route.path.startsWith(`${stepRoute}/`)
})
</script>
